import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { portalApi } from "@/lib/portalApi";
import { toast } from "sonner";
import CanonicalUploader from "@/components/documents/CanonicalUploader";

const STATUS_ICON = { complete: "CheckCircle2", in_progress: "CircleDotDashed", not_started: "Circle" };
const STATUS_COLOR = { complete: "text-emerald-600", in_progress: "text-amber-500", not_started: "text-slate-300" };

// LARI reliability tier → client-safe presentation (Phase 2B)
const LARI_TIER_CONFIG = {
  staff_verified:     { text: "Previously verified by your tax team.",      icon: "ShieldCheck",  cls: "bg-emerald-50 border-emerald-200 text-emerald-800", iconCls: "text-emerald-500" },
  client_confirmed:   { text: "Previously confirmed by you.",               icon: "UserCheck",    cls: "bg-blue-50 border-blue-200 text-blue-800",           iconCls: "text-blue-500"   },
  ai_generated:       { text: "Previously collected \u2014 please verify.", icon: "Sparkles",     cls: "bg-amber-50 border-amber-200 text-amber-800",        iconCls: "text-amber-500"  },
  verify_recommended: { text: "Please review and confirm.",                 icon: "AlertCircle",  cls: "bg-orange-50 border-orange-200 text-orange-800",     iconCls: "text-orange-500" },
  unknown:            { text: "Pre-filled from your profile.",              icon: "Info",         cls: "bg-slate-50 border-slate-200 text-slate-600",        iconCls: "text-slate-400"  },
};

function LariBadge({ tier, code, suffix = "" }) {
  const cfg = LARI_TIER_CONFIG[tier] || LARI_TIER_CONFIG.unknown;
  const Ic  = Icons[cfg.icon] || Icons.Info;
  return (
    <div className={`mt-1 inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs ${cfg.cls}`}
      data-testid={`psr-lari-badge-${code}${suffix ? `-${suffix}` : ""}`}>
      <Ic className={`h-3 w-3 ${cfg.iconCls} shrink-0`} />
      {cfg.text}
    </div>
  );
}

function HistoricalNote({ value, code, suffix = "" }) {
  if (!value) return null;
  return (
    <div className="mt-1.5 flex items-start gap-1.5 text-xs text-slate-500"
      data-testid={`psr-history-note-${code}${suffix ? `-${suffix}` : ""}`}>
      <Icons.History className="h-3 w-3 text-slate-400 mt-0.5 shrink-0" />
      <span>Based on your profile: <span className="font-medium text-slate-700">{String(value)}</span>. Please confirm whether this applies for the current period.</span>
    </div>
  );
}

// STR stage → visual representation for the contextual mirror
const STR_STAGE_STEPS = [
  { key: "questionnaire",          label: "Organizer",           icon: "ClipboardList" },
  { key: "strategic_analysis",     label: "Strategic Analysis",  icon: "SearchCode"    },
  { key: "additional_information", label: "Additional Info",     icon: "MessageSquare" },
  { key: "report_preparation",     label: "Report Preparation",  icon: "FileText"      },
  { key: "report_ready",           label: "Report Ready",        icon: "FileCheck2"    },
  { key: "completed",              label: "Completed",           icon: "CheckCircle2"  },
];
const STR_ACTIVE_STAGES = new Set(["questionnaire","strategic_analysis","additional_information","report_preparation","report_ready","completed"]);

const RESP_PILL = {
  client:      "bg-amber-50 border-amber-200 text-amber-700",
  firm:        "bg-blue-50 border-blue-200 text-blue-700",
  third_party: "bg-slate-50 border-slate-200 text-slate-600",
  complete:    "bg-emerald-50 border-emerald-200 text-emerald-700",
};

function StrLifecycleBanner({ card }) {
  if (!card) return null;
  const resp       = card.responsibility?.actor || "firm";
  const stageKey   = card.stage?.key;
  const stageIdx   = STR_STAGE_STEPS.findIndex((s) => s.key === stageKey);
  const isComplete = card.is_complete;
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid="str-lifecycle-banner">
      <div className="flex items-center gap-2 mb-4">
        <Icons.Route className="h-4 w-4 text-slate-400" />
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Review Progress</span>
        <span className={`ml-auto flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border ${RESP_PILL[resp]}`}
          data-testid="str-banner-resp">
          {card.responsibility?.label}
        </span>
      </div>
      {/* Stage stepper */}
      <div className="flex items-center gap-0.5 mb-4" data-testid="str-banner-steps">
        {STR_STAGE_STEPS.map((step, idx) => {
          const done    = idx < stageIdx || isComplete;
          const current = idx === stageIdx && !isComplete;
          const Ic      = Icons[step.icon] || Icons.Circle;
          return (
            <div key={step.key} className="flex items-center flex-1 min-w-0">
              <div className={`flex flex-col items-center flex-1 min-w-0 ${idx <= stageIdx || isComplete ? "" : "opacity-40"}`}>
                <div className={`h-7 w-7 rounded-full flex items-center justify-center mb-1
                  ${done ? "bg-emerald-500" : current ? "bg-[#F5A623]" : "bg-slate-200"}`}>
                  <Ic className={`h-3.5 w-3.5 ${done ? "text-white" : current ? "text-[#171B2E]" : "text-slate-400"}`} />
                </div>
                <span className={`text-[9px] text-center leading-tight truncate max-w-full px-0.5
                  ${current ? "text-[#171B2E] font-semibold" : done ? "text-emerald-600" : "text-slate-400"}`}>
                  {step.label}
                </span>
              </div>
              {idx < STR_STAGE_STEPS.length - 1 && (
                <div className={`h-px w-3 shrink-0 -mt-3 ${idx < stageIdx || isComplete ? "bg-emerald-400" : "bg-slate-200"}`} />
              )}
            </div>
          );
        })}
      </div>
      {/* Status message */}
      <p className="text-sm text-slate-600 leading-relaxed" data-testid="str-banner-detail">
        {card.stage?.detail}
      </p>
      {card.outstanding_action?.exists && card.outstanding_action?.link && (
        <Link to={card.outstanding_action.link} data-testid="str-banner-cta"
          className="mt-3 inline-flex items-center gap-1.5 bg-[#171B2E] hover:bg-[#222842] text-white text-xs font-semibold rounded-lg px-3 py-2 transition-colors">
          {card.outstanding_action.cta_label}
          <Icons.ArrowRight className="h-3.5 w-3.5 text-[#F5A623]" />
        </Link>
      )}
    </div>
  );
}

export default function PortalStrategicReview({ preview = null } = {}) {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const deepLinkCode = sp.get("question");       // Phase 1D: ?question={code} deep-link from STR finding
  const questionRefs = useRef({});               // { [code]: domElement }
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);          // {review, state, document_index}
  const [answers, setAnswers] = useState({});
  const [activeSection, setActiveSection] = useState(0);
  const [saving, setSaving] = useState("saved");    // saved | saving
  const [reviewOpen, setReviewOpen] = useState(false);
  const [review, setReview] = useState(null);
  const [certified, setCertified] = useState(false);
  const [certName, setCertName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lifecycleCard, setLifecycleCard] = useState(null); // STR lifecycle projection
  const [viewingAnswers, setViewingAnswers] = useState(false); // P0 Viewer: toggle read-only submission view
  const [subSection, setSubSection] = useState(0);          // P0 Viewer: active section in submission view
  const saveTimer = useRef(null);

  const load = useCallback(async () => {
    if (preview) {
      setData(preview);
      if (preview.state) {
        const a = {};
        preview.state.sections.forEach((s) => s.questions.forEach((q) => { if (q.value !== undefined && q.value !== null) a[q.code] = q.value; }));
        setAnswers(a);
      }
      setLoading(false);
      return;
    }
    try {
      const { data } = await portalApi.get("/portal/strategic-review");
      setData(data);
      if (data.review && data.state) {
        const a = {};
        data.state.sections.forEach((s) => s.questions.forEach((q) => { if (q.value !== undefined && q.value !== null) a[q.code] = q.value; }));
        setAnswers(a);
      }
    } catch { toast.error("Could not load your Strategic Review"); }
    finally { setLoading(false); }
  }, [preview]);

  // Fetch lifecycle card from canonical projection (same source as dashboard)
  useEffect(() => {
    if (preview) return;
    portalApi.get("/portal/engagements-status")
      .then((r) => {
        const cards = r.data?.engagements || [];
        const strCard = cards.find((c) => c.engagement_type === "strategic_review");
        if (strCard) setLifecycleCard(strCard);
      })
      .catch(() => {});
  }, [preview]);

  useEffect(() => { load(); }, [load]);

  const pushSave = useCallback((next) => {
    setSaving("saving");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const { data: res } = await portalApi.post("/portal/strategic-review/answers", { answers: next });
        setData((d) => ({ ...d, state: res.state, document_index: res.document_index,
                          review: { ...d.review, status: res.status } }));
        setSaving("saved");
      } catch (e) {
        setSaving("saved");
        toast.error(e?.response?.data?.detail || "Save failed");
      }
    }, 550);
  }, []);

  const setAnswer = (code, value) => {
    if (preview) return;
    setAnswers((prev) => { const next = { ...prev, [code]: value }; pushSave({ [code]: value }); return next; });
  };

  const setDna = async (code, enabled) => {
    if (preview) return;
    setSaving("saving");
    try {
      const { data: res } = await portalApi.post("/portal/strategic-review/does-not-apply",
        { question_code: code, enabled });
      // Update state directly from server response (avoids full reload latency)
      setData((d) => ({ ...d, state: res.state,
                        document_index: res.document_index || d.document_index,
                        review: { ...d.review, status: res.status } }));
      setSaving("saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
      setSaving("saved");
    }
  };

  const state = data?.state;
  const sections = state?.sections || [];
  const visibleSections = useMemo(() => sections.filter((s) => s.questions.some((q) => q.visible)), [sections]);
  const current = visibleSections[activeSection] || visibleSections[0];

  // Phase 1D: deep-link — when ?question={code} is present, navigate to the section
  // containing that question and scroll/highlight it after render.
  useEffect(() => {
    if (!deepLinkCode || !visibleSections.length) return;
    const sectionIdx = visibleSections.findIndex((s) => s.questions.some((q) => q.code === deepLinkCode));
    if (sectionIdx >= 0 && sectionIdx !== activeSection) setActiveSection(sectionIdx);
    // Scroll to question element after a render tick
    const t = setTimeout(() => {
      const el = questionRefs.current[deepLinkCode];
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("ring-2", "ring-amber-400"); }
    }, 300);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkCode, visibleSections.length]);

  const docsForQuestion = useCallback((qcode) => {
    const idx = data?.document_index || {};
    const out = {};
    Object.entries(idx).forEach(([k, v]) => {
      const [, qc, ri] = k.split("|");
      if (qc === qcode) out[ri] = v;
    });
    return out;
  }, [data]);

  const uploadFor = async (requestId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("request_id", requestId);
    try {
      await portalApi.post("/portal/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      await load();
    } catch { toast.error("Upload failed"); }
  };

  const openReview = async () => {
    try {
      const { data } = await portalApi.get("/portal/strategic-review/review-summary");
      setReview(data); setCertName(""); setCertified(false); setReviewOpen(true);
    } catch { toast.error("Could not build your review summary"); }
  };
  const doSubmit = async () => {
    setSubmitting(true);
    try {
      await portalApi.post("/portal/strategic-review/submit", { certified: true, certification_name: certName });
      toast.success("Strategic Review submitted — thank you!");
      setReviewOpen(false); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Submission failed"); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="p-10 text-center text-slate-400" data-testid="psr-loading">Loading your Strategic Review Organizer…</div>;
  if (!data?.review) return (
    <div className="max-w-xl mx-auto text-center py-20" data-testid="psr-empty">
      <Icons.Compass className="h-12 w-12 mx-auto text-slate-300" />
      <h1 className="text-xl font-semibold text-slate-800 mt-4">No Strategic Review yet</h1>
      <p className="text-slate-500 mt-2">Your tax team hasn't made a Strategic Review available to you yet. We'll let you know when it's ready.</p>
      <button onClick={() => navigate("/portal")} className="mt-6 text-blue-600 font-medium" data-testid="psr-back">Back to Dashboard</button>
    </div>
  );

  if (data.open_round) return <Round2View data={data} reload={load} navigate={navigate} lifecycleCard={lifecycleCard} />;

  if (data.review.status === "submitted") {
    // P0 Viewer: compute submission viewer state from canonical data
    const subSections = (data?.state?.sections || []).filter((s) => (s.questions || []).some((q) => q.visible));
    const subCurrent = subSections[subSection] || subSections[0];
    const subVisibleTotal = subSections.reduce((n, s) => n + (s.questions || []).filter((q) => q.visible).length, 0);
    const subAnsweredTotal = subSections.reduce((n, s) => n + (s.questions || []).filter((q) => q.visible && q.answered).length, 0);
    const fmtDateLong = (iso) => iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : null;

    const docsForQuestion = (code) => {
      const byRecIdx = {};
      Object.entries(data.document_index || {}).forEach(([k, v]) => {
        const [, qc, ri] = k.split("|");
        if (qc === code) byRecIdx[ri === "None" ? "None" : (ri || "None")] = v;
      });
      return byRecIdx;
    };

    if (viewingAnswers && subSections.length > 0) return (
      <div className="max-w-5xl mx-auto py-6" data-testid="psr-submission-viewer">
        {/* Viewer header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Icons.FileCheck2 className="h-5 w-5 text-emerald-600" />
              <h1 className="text-xl font-semibold text-slate-900">Your Submitted Answers</h1>
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium border border-emerald-200"
                data-testid="psr-sub-status-badge">Submitted</span>
            </div>
            {data.review.submitted_at && (
              <p className="text-sm text-slate-500" data-testid="psr-sub-date">
                Submitted {fmtDateLong(data.review.submitted_at)}
                {data.review.certification_name && <> · Certified by <span className="font-medium text-slate-700">{data.review.certification_name}</span></>}
              </p>
            )}
            <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
              <Icons.Lock className="h-3 w-3" />Read-only — your original submission is preserved and cannot be changed.
            </p>
          </div>
          <button onClick={() => setViewingAnswers(false)}
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 border border-slate-200 rounded-xl px-3 py-1.5"
            data-testid="psr-sub-back-btn">
            <Icons.ArrowLeft className="h-4 w-4" />Back
          </button>
        </div>

        {/* Progress summary */}
        <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 flex items-center gap-4">
          <div className="text-sm text-slate-600">
            <span className="font-semibold text-slate-800" data-testid="psr-sub-answered">{subAnsweredTotal}</span> of <span className="font-semibold text-slate-800" data-testid="psr-sub-total">{subVisibleTotal}</span> questions answered
          </div>
          <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${subVisibleTotal > 0 ? Math.round((subAnsweredTotal / subVisibleTotal) * 100) : 0}%` }} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
          {/* Section nav sidebar */}
          <aside className="lg:col-span-1">
            <div className="bg-white border border-slate-200 rounded-2xl p-4 sticky top-4" data-testid="psr-sub-section-nav">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">Sections</p>
              <nav className="space-y-1">
                {subSections.map((s, i) => {
                  const done = (s.questions || []).filter((q) => q.visible && q.answered).length;
                  const total = (s.questions || []).filter((q) => q.visible).length;
                  return (
                    <button key={s.key} onClick={() => setSubSection(i)}
                      className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm ${i === subSection ? "bg-slate-100 font-medium text-slate-900" : "text-slate-600 hover:bg-slate-50"}`}
                      data-testid={`psr-sub-nav-${s.key}`}>
                      {done === total && total > 0
                        ? <Icons.CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                        : <Icons.Circle className="h-4 w-4 shrink-0 text-slate-300" />}
                      <span className="truncate">{s.title}</span>
                      <span className="ml-auto text-[10px] text-slate-400 shrink-0">{done}/{total}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Current section */}
          <main className="lg:col-span-3">
            {subCurrent && (
              <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid={`psr-sub-section-${subCurrent.key}`}>
                <div className="flex items-center gap-3 mb-1">
                  {(() => { const I = Icons[subCurrent.icon] || Icons.FileText; return <I className="h-5 w-5 text-slate-400" />; })()}
                  <h2 className="text-lg font-semibold text-slate-900">{subCurrent.title}</h2>
                </div>
                {subCurrent.intro && <p className="text-slate-500 text-sm mb-5">{subCurrent.intro}</p>}
                <div className="space-y-6">
                  {(subCurrent.questions || []).filter((q) => q.visible).map((q) => {
                    const isDna = q.answer_status === "does_not_apply";
                    const val = answers[q.code] ?? q.value;
                    const docs = docsForQuestion(q.code);
                    const rt = q.response_type;

                    const renderVal = () => {
                      if (isDna) return <span className="text-slate-400 italic text-sm">Does not apply</span>;
                      if (val === null || val === undefined || val === "") return <span className="text-slate-300 italic text-sm">Not answered</span>;
                      if (rt === "yes_no" || rt === "yes_no_unknown") {
                        const C = { yes: "bg-emerald-100 text-emerald-800 border-emerald-200", no: "bg-rose-100 text-rose-700 border-rose-200", unknown: "bg-slate-100 text-slate-600 border-slate-200" };
                        return <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium capitalize border ${C[val] || "bg-slate-100 text-slate-700 border-slate-200"}`}>{val}</span>;
                      }
                      if (rt === "multi_select" && Array.isArray(val))
                        return <div className="flex flex-wrap gap-1.5">{val.map((v, i) => <span key={i} className="px-2.5 py-0.5 rounded-full text-xs bg-slate-100 text-slate-700 border border-slate-200">{v}</span>)}</div>;
                      if (rt === "repeating_record" && Array.isArray(val)) {
                        if (!val.length) return <span className="text-slate-300 italic text-sm">No entries</span>;
                        return <div className="space-y-2">{val.map((rec, i) => <div key={i} className="border border-slate-100 rounded-xl p-3 bg-slate-50 text-xs text-slate-600">{Object.entries(rec).filter(([, v]) => v).map(([k, v]) => <div key={k}><span className="text-slate-400">{k.replace(/_/g, " ")}: </span>{String(v)}</div>)}</div>)}</div>;
                      }
                      if (rt === "date" && val) { try { return <span className="text-sm text-slate-700">{new Date(val).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>; } catch { /* fallthrough */ } }
                      if (rt === "document_request") return null;
                      return <span className="text-sm text-slate-700 whitespace-pre-wrap">{String(val)}</span>;
                    };

                    const docEntries = Object.values(docs);
                    const docStatusColor = { uploaded: "bg-emerald-50 border-emerald-200 text-emerald-700", awaiting_review: "bg-emerald-50 border-emerald-200 text-emerald-600", approved: "bg-emerald-50 border-emerald-200 text-emerald-700", missing: "bg-amber-50 border-amber-200 text-amber-700" };

                    return (
                      <div key={q.code} className="pb-5 border-b border-slate-50 last:border-0" data-testid={`psr-sub-q-${q.code}`}>
                        <label className="block text-sm font-medium text-slate-800 mb-1.5">
                          {q.text}{q.required && <span className="text-rose-500 ml-1">*</span>}
                        </label>
                        {q.help_text && <p className="text-xs text-slate-400 mb-1.5">{q.help_text}</p>}
                        {isDna ? <span className="text-slate-400 italic text-sm">Does not apply</span> : renderVal()}
                        {docEntries.length > 0 && (
                          <div className="mt-2 space-y-1.5">
                            {docEntries.map((d) => (
                              <div key={d.id} className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-xs ${docStatusColor[d.status] || "bg-slate-50 border-slate-200 text-slate-600"}`}
                                data-testid={`psr-sub-doc-${d.id}`}>
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <Icons.Paperclip className="h-3.5 w-3.5 shrink-0" />
                                  <span className="truncate font-medium">{d.title}</span>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <span className="font-semibold capitalize">{d.status}</span>
                                  {d.linked_document_id && (
                                    <a href={`/api/portal/documents/${d.linked_document_id}/content`}
                                      target="_blank" rel="noopener noreferrer"
                                      className="underline hover:no-underline" data-testid={`psr-sub-doc-view-${d.id}`}>View</a>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-between mt-6 pt-5 border-t border-slate-100">
                  <button disabled={subSection === 0} onClick={() => setSubSection((i) => Math.max(0, i - 1))}
                    className="inline-flex items-center gap-1 text-slate-500 disabled:opacity-40 text-sm"
                    data-testid="psr-sub-prev">
                    <Icons.ArrowLeft className="h-4 w-4" />Previous
                  </button>
                  {subSection < subSections.length - 1 ? (
                    <button onClick={() => setSubSection((i) => i + 1)}
                      className="inline-flex items-center gap-1 bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 rounded-xl text-sm font-medium"
                      data-testid="psr-sub-next">
                      Continue<Icons.ArrowRight className="h-4 w-4" />
                    </button>
                  ) : (
                    <span className="text-xs text-slate-400 italic">End of your submission</span>
                  )}
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    );

    return (
      <div className="max-w-2xl mx-auto py-12" data-testid="psr-submitted">
        <div className="text-center mb-8">
          <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
            <Icons.CheckCircle2 className="h-9 w-9 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-semibold text-slate-800 mt-5">Organizer submitted</h1>
          {data.review.submitted_at && (
            <p className="text-sm text-slate-400 mt-1" data-testid="psr-submitted-date">
              {fmtDateLong(data.review.submitted_at)}
            </p>
          )}
          <p className="text-slate-500 mt-2">
            Thank you — your Strategic Tax Review Organizer has been certified and sent to your tax team.
          </p>
        </div>
        {/* STR lifecycle mirror — same canonical projection used by the Dashboard */}
        <StrLifecycleBanner card={lifecycleCard} />
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          {subSections.length > 0 && (
            <button onClick={() => { setSubSection(0); setViewingAnswers(true); }}
              className="inline-flex items-center gap-2 bg-white border border-slate-300 text-slate-700 hover:border-slate-400 px-5 py-2.5 rounded-xl text-sm font-medium"
              data-testid="psr-view-answers-btn">
              <Icons.Eye className="h-4 w-4" />View Your Submitted Answers
            </button>
          )}
          <button onClick={() => navigate("/portal/strategic-reviews")}
            className="inline-flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 rounded-xl text-sm font-medium"
            data-testid="psr-done">
            Back to Strategic Reviews
          </button>
        </div>
      </div>
    );
  }

  const isOrganizer = data.review?.is_str_organizer;
  const prefillMeta  = data.review?.prefill_metadata || {};
  const unconfirmedPrefills = Object.values(prefillMeta).filter((m) => !m.confirmed).length;
  const pageTitle    = isOrganizer ? "Strategic Tax Review Organizer" : "Strategic Tax Review";
  const pageSubtitle = isOrganizer
    ? "Answer at your own pace — everything saves automatically. Your advisory team can begin work while you complete this."
    : "Answer at your own pace — everything saves automatically.";

  return (
    <div className="max-w-6xl mx-auto" data-testid="portal-strategic-review">
      {/* Organizer intro banner */}
      {isOrganizer && (
        <div className="mb-5 bg-indigo-50 border border-indigo-200 rounded-2xl p-4 flex items-start gap-3" data-testid="psr-organizer-banner">
          <Icons.Compass className="h-5 w-5 text-indigo-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-indigo-900">Strategic Tax Review Organizer</p>
            <p className="text-sm text-indigo-700 mt-0.5">
              Your advisory team can start your review while you complete this. There is no deadline — complete as much as you can, then submit.
              {unconfirmedPrefills > 0 && (
                <> Some answers have been <span className="font-medium">pre-filled from your profile</span> — please confirm or update them.</>
              )}
            </p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 font-heading" data-testid="psr-title">{pageTitle}</h1>
          <p className="text-slate-500 text-sm mt-0.5">{pageSubtitle}</p>
        </div>
        <div className="flex items-center gap-3">
          {!preview && <span className="text-xs text-slate-400 flex items-center gap-1" data-testid="psr-save-state">
            {saving === "saving" ? <><Icons.Loader2 className="h-3.5 w-3.5 animate-spin" />Saving…</> : <><Icons.Check className="h-3.5 w-3.5 text-emerald-500" />Saved</>}
          </span>}
          {!preview && <button onClick={openReview} className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-xl text-sm font-medium" data-testid="psr-review-btn">
            <Icons.ListChecks className="h-4 w-4" />Review &amp; Submit
          </button>}
          {preview && <span className="text-xs text-slate-400" data-testid="psr-preview-tag">Preview — read only</span>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Section progress sidebar */}
        <aside className="lg:col-span-1">
          <div className="bg-white border border-slate-200 rounded-2xl p-4 sticky top-4" data-testid="psr-progress">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-wide text-slate-400">Progress</span>
              <span className="text-lg font-semibold text-slate-800" data-testid="psr-progress-pct">{state.progress}%</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-4">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${state.progress}%` }} />
            </div>
            <nav className="space-y-1">
              {visibleSections.map((s, i) => {
                const Ico = Icons[STATUS_ICON[s.status]] || Icons.Circle;
                return (
                  <button key={s.key} onClick={() => setActiveSection(i)}
                    className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm ${i === activeSection ? "bg-slate-100 font-medium text-slate-900" : "text-slate-600 hover:bg-slate-50"}`}
                    data-testid={`psr-section-nav-${s.key.toLowerCase().replace(/\s+/g, "-")}`}>
                    <Ico className={`h-4 w-4 shrink-0 ${STATUS_COLOR[s.status]}`} />
                    <span className="truncate">{s.title}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Current section */}
        <main className="lg:col-span-3">
          {current && (
            <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="psr-section">
              <div className="flex items-center gap-3 mb-1">
                {(() => { const I = Icons[current.icon] || Icons.FileText; return <I className="h-5 w-5 text-slate-400" />; })()}
                <h2 className="text-lg font-semibold text-slate-900">{current.title}</h2>
              </div>
              {current.intro && <p className="text-slate-500 text-sm mb-6">{current.intro}</p>}

              <div className="space-y-7">
                {current.questions.filter((q) => q.visible).map((q) => (
                  <div key={q.code} ref={(el) => { questionRefs.current[q.code] = el; }}
                    className="rounded-lg transition-all duration-300"
                    data-testid={`psr-question-${q.code}`}>
                    <QuestionField q={q} value={answers[q.code]} onChange={(v) => setAnswer(q.code, v)}
                      onDna={(enabled) => setDna(q.code, enabled)}
                      docs={docsForQuestion(q.code)} onUploaded={load}
                      prefillMeta={prefillMeta[q.code]} />
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between mt-8 pt-5 border-t border-slate-100">
                <button disabled={activeSection === 0} onClick={() => setActiveSection((i) => Math.max(0, i - 1))}
                  className="inline-flex items-center gap-1 text-slate-500 disabled:opacity-40 text-sm" data-testid="psr-prev">
                  <Icons.ChevronLeft className="h-4 w-4" />Previous
                </button>
                {activeSection < visibleSections.length - 1 ? (
                  <button onClick={() => setActiveSection((i) => i + 1)} className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl text-sm font-medium" data-testid="psr-next">
                    Continue<Icons.ChevronRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button onClick={openReview} disabled={!!preview} className="inline-flex items-center gap-1 bg-slate-900 text-white px-5 py-2.5 rounded-xl text-sm font-medium disabled:opacity-50" data-testid="psr-finish">
                    Review &amp; Submit<Icons.ListChecks className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          )}
        </main>
      </div>

      {reviewOpen && review && (
        <ReviewModal review={review} certified={certified} setCertified={setCertified} certName={certName}
          setCertName={setCertName} submitting={submitting} onClose={() => setReviewOpen(false)} onSubmit={doSubmit} />
      )}
    </div>
  );
}

function QuestionField({ q, value, onChange, onDna, docs, onUploaded, prefillMeta }) {
  const hasPrefill = !!prefillMeta;
  const prefillConfirmed = prefillMeta?.confirmed;
  const prefillOriginal = prefillMeta?.original_value;
  // Deep equality for arrays (multi_select); reference equality for scalars.
  const _valEqual = Array.isArray(value) && Array.isArray(prefillOriginal)
    ? JSON.stringify([...value].sort()) === JSON.stringify([...prefillOriginal].sort())
    : value === prefillOriginal;
  const isChanged = hasPrefill && value !== undefined && !_valEqual;
  // DNA state comes from server-computed q.answer_status
  const isDna = q.answer_status === "does_not_apply";
  const allowDna = q.allow_dna === true;

  const label = (
    <div className="mb-1.5">
      <label className="block font-medium text-slate-800" data-testid={`psr-q-${q.code}`}>
        {q.text}{q.required && <span className="text-rose-500 ml-1">*</span>}
      </label>
      {hasPrefill && !isChanged && !prefillConfirmed && (
        <LariBadge tier={prefillMeta?.source_field_status || "unknown"} code={q.code} />
      )}
      {hasPrefill && prefillConfirmed && !isChanged && (
        <div className="mt-1 inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1 text-xs text-emerald-800" data-testid={`psr-prefill-confirmed-${q.code}`}>
          <Icons.CheckCircle2 className="h-3 w-3 text-emerald-500" />Profile value confirmed
        </div>
      )}
      {isChanged && (
        <div className="mt-1 inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-lg px-2.5 py-1 text-xs text-blue-800" data-testid={`psr-prefill-changed-${q.code}`}>
          <Icons.RefreshCw className="h-3 w-3 text-blue-500" />Updated — your team will review this change
        </div>
      )}
      {hasPrefill && prefillMeta?.requires_current_year_confirmation && !isChanged && (
        <HistoricalNote value={prefillMeta?.historical_value} code={q.code} />
      )}
    </div>
  );
  const help = q.help_text && <p className="text-xs text-slate-400 mb-2">{q.help_text}</p>;
  const rt = q.response_type;

  const body = (() => {
    if (rt === "yes_no" || rt === "yes_no_unknown") {
      const opts = rt === "yes_no_unknown" ? ["yes", "no", "unknown"] : ["yes", "no"];
      return (
        <div className="flex gap-2" data-testid={`psr-field-${q.code}`}>
          {opts.map((o) => (
            <button key={o} onClick={() => onChange(o)}
              className={`px-4 py-2 rounded-xl text-sm font-medium capitalize border ${value === o ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
              data-testid={`psr-opt-${q.code}-${o}`}>{o}</button>
          ))}
        </div>
      );
    }
    if (rt === "long_text") return <textarea rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm" data-testid={`psr-field-${q.code}`} />;
    if (rt === "single_select") return (
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white" data-testid={`psr-field-${q.code}`}>
        <option value="">Select…</option>
        {(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
    if (rt === "multi_select") return (
      <div className="flex flex-wrap gap-2" data-testid={`psr-field-${q.code}`}>
        {(q.options || []).map((o) => {
          const on = (value || []).includes(o);
          return <button key={o} onClick={() => onChange(on ? value.filter((x) => x !== o) : [...(value || []), o])}
            className={`px-3 py-1.5 rounded-full text-sm border ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}>{o}</button>;
        })}
      </div>
    );
    if (rt === "repeating_record") return <RecordList q={q} value={value || []} onChange={onChange} docs={docs} onUploaded={onUploaded} />;
    if (rt === "document_request") return <InlineDoc entry={docs["None"]} onUploaded={onUploaded} />;
    const type = rt === "date" ? "date" : (rt === "amount" || rt === "percentage" ? "number" : "text");
    return <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm" data-testid={`psr-field-${q.code}`} />;
  })();

  return (
    <div>
      {label}
      {/* DNA control: shown for eligible question types */}
      {allowDna && (
        <div className="flex items-center justify-end mb-1.5">
          <button type="button" onClick={() => onDna && onDna(!isDna)}
            data-testid={`psr-q-${q.code}-dna`}
            className={`text-[11px] font-medium rounded-full px-2.5 py-1 border transition-colors ${isDna ? "bg-slate-700 text-white border-slate-700" : "border-slate-200 text-slate-500 hover:border-slate-400"}`}>
            {isDna ? "✓ Does not apply" : "Does not apply"}
          </button>
        </div>
      )}
      {isDna ? (
        <p className="text-sm text-slate-400 italic py-2" data-testid={`psr-dna-${q.code}`}>
          Marked as not applicable to your situation.
        </p>
      ) : (
        <>
          {help}{body}
        </>
      )}
      {!isDna && rt !== "repeating_record" && rt !== "document_request" && docs["None"] && (
        <div className="mt-3"><InlineDoc entry={docs["None"]} onUploaded={onUploaded} /></div>
      )}
    </div>
  );
}

function RecordList({ q, value, onChange, docs, onUploaded }) {
  const fields = q.fields || [];
  const addRecord = () => onChange([...(value || []), {}]);
  const updateRecord = (i, key, v) => { const next = value.map((r, idx) => idx === i ? { ...r, [key]: v } : r); onChange(next); };
  const removeRecord = (i) => onChange(value.filter((_, idx) => idx !== i));
  const summaryOf = (rec) => (q.record_summary || []).map((f) => rec[f]).filter(Boolean).join(" · ");

  return (
    <div className="space-y-3" data-testid={`psr-records-${q.code}`}>
      {(value || []).map((rec, i) => (
        <div key={i} className="border border-slate-200 rounded-xl p-4 bg-slate-50/50" data-testid={`psr-record-${q.code}-${i}`}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{summaryOf(rec) || `Entry ${i + 1}`}</span>
            <button onClick={() => removeRecord(i)} className="text-rose-500 hover:text-rose-600" data-testid={`psr-record-remove-${q.code}-${i}`}><Icons.Trash2 className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {fields.map((f) => (
              <div key={f.key}>
                <label className="text-[11px] text-slate-400">{f.label}</label>
                {f.type === "select" ? (
                  <select value={rec[f.key] || ""} onChange={(e) => updateRecord(i, f.key, e.target.value)} className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm bg-white mt-0.5" data-testid={`psr-recfield-${q.code}-${i}-${f.key}`}>
                    <option value="">Select…</option>{(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : f.type === "yes_no" ? (
                  <div className="flex gap-1.5 mt-1">
                    {["yes", "no"].map((o) => <button key={o} onClick={() => updateRecord(i, f.key, o)} className={`px-3 py-1 rounded-lg text-xs capitalize border ${rec[f.key] === o ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600"}`}>{o}</button>)}
                  </div>
                ) : f.type === "multiselect" ? (
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {(f.options || []).map((o) => { const on = (rec[f.key] || []).includes(o); return <button key={o} onClick={() => updateRecord(i, f.key, on ? rec[f.key].filter((x) => x !== o) : [...(rec[f.key] || []), o])} className={`px-2.5 py-1 rounded-full text-xs border ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-200 text-slate-600"}`}>{o}</button>; })}
                  </div>
                ) : (
                  <input type={f.type === "amount" ? "number" : (f.type === "date" ? "date" : "text")} value={rec[f.key] || ""} onChange={(e) => updateRecord(i, f.key, e.target.value)} placeholder={f.placeholder || ""} className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm mt-0.5" data-testid={`psr-recfield-${q.code}-${i}-${f.key}`} />
                )}
              </div>
            ))}
          </div>
          {docs[String(i)] && <div className="mt-3"><InlineDoc entry={docs[String(i)]} onUploaded={onUploaded} /></div>}
        </div>
      ))}
      <button onClick={addRecord} className="inline-flex items-center gap-1.5 text-sm text-blue-600 font-medium border border-dashed border-blue-300 rounded-xl px-4 py-2 hover:bg-blue-50 w-full justify-center" data-testid={`psr-record-add-${q.code}`}>
        <Icons.Plus className="h-4 w-4" />Add {q.record_summary ? "Another" : "Entry"}
      </button>
    </div>
  );
}

function InlineDoc({ entry, onUploaded }) {
  const [open, setOpen] = useState(false);
  if (!entry) return null;
  const done = ["uploaded", "awaiting_review", "approved"].includes(entry.status);
  const uploadFn = (file, { signal, asVersion, note, onProgress }) => {
    const fd = new FormData();
    fd.append("file", file); fd.append("request_id", entry.id);
    if (asVersion) fd.append("as_version", "true");
    if (note) fd.append("version_note", note);
    return portalApi.post("/portal/documents/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" }, signal,
      onUploadProgress: (e) => { if (e.total) onProgress(Math.round((e.loaded / e.total) * 100)); },
    }).then((r) => r.data).catch((err) => {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === "duplicate_document") {
        const e2 = new Error("Already uploaded — keep as a new version?"); e2.duplicate = detail.existing; throw e2;
      }
      throw new Error("Upload failed");
    });
  };
  return (
    <div className={`rounded-xl px-3 py-2 border ${done ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid={`psr-doc-${entry.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm min-w-0">
          {done ? <Icons.CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" /> : <Icons.Paperclip className="h-4 w-4 text-amber-600 shrink-0" />}
          <span className="truncate text-slate-700">{entry.title}{entry.required && !done && <span className="text-rose-500 ml-1">(required)</span>}</span>
        </div>
        <div className="shrink-0">
          {done ? <span className="text-xs text-emerald-700 font-medium">Uploaded ✓</span> : (
            <button onClick={() => setOpen((s) => !s)} className="text-xs bg-white border border-slate-300 rounded-lg px-3 py-1.5 font-medium hover:border-slate-400" data-testid={`psr-doc-upload-${entry.id}`}>Upload</button>
          )}
        </div>
      </div>
      {!done && open && (
        <div className="mt-2" data-testid={`psr-doc-uploader-${entry.id}`}>
          <CanonicalUploader compact uploadFn={uploadFn} allowSeparate={false} onFileUploaded={() => onUploaded && onUploaded()} testId={`psr-uploader-${entry.id}`} />
        </div>
      )}
    </div>
  );
}

function ReviewModal({ review, certified, setCertified, certName, setCertName, submitting, onClose, onSubmit }) {
  const ok = review.validation?.ok;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="psr-review-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-slate-900">Please review before you certify</h2>
          <button onClick={onClose}><Icons.X className="h-5 w-5 text-slate-400" /></button>
        </div>
        {!ok && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl px-4 py-3 text-sm mb-4" data-testid="psr-review-incomplete">
            {review.validation.missing_count} required question(s) still need an answer before you can submit.
          </div>
        )}
        <p className="text-slate-600 text-sm mb-2 font-medium">You told us:</p>
        <ul className="space-y-1.5 mb-5" data-testid="psr-review-summary">
          {review.summary.map((b, i) => <li key={i} className="flex gap-2 text-sm text-slate-700"><Icons.Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />{b}</li>)}
        </ul>
        {review.outstanding_documents?.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-5">
            <p className="text-sm font-medium text-amber-800 mb-1">Documents still needed (you can upload these anytime):</p>
            <ul className="text-sm text-amber-700 list-disc pl-5">{review.outstanding_documents.map((d, i) => <li key={i}>{d.title}</li>)}</ul>
          </div>
        )}
        <div className="border-t border-slate-100 pt-4">
          <label className="flex items-start gap-2 text-sm text-slate-700 mb-3">
            <input type="checkbox" checked={certified} onChange={(e) => setCertified(e.target.checked)} className="mt-1" data-testid="psr-certify-check" disabled={!ok} />
            I certify that the information above is true and complete to the best of my knowledge.
          </label>
          <input value={certName} onChange={(e) => setCertName(e.target.value)} placeholder="Type your full name to sign" className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm mb-4" data-testid="psr-certify-name" />
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-slate-500 text-sm">Not yet</button>
            <button onClick={onSubmit} disabled={!ok || !certified || !certName.trim() || submitting}
              className="inline-flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 rounded-xl text-sm font-medium disabled:opacity-40" data-testid="psr-submit-btn">
              {submitting ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Send className="h-4 w-4" />}Submit Review
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// P0: Structured supplemental question — three answer states replace the plain textarea.
// All three count as responded for submission; Allison receives the semantic state.
function SupplementalQuestion({ code, questionText, value, onChange, required, prefillMeta }) {
  const getType = (v) => {
    if (v == null) return null;
    if (typeof v === "object") return v.type;
    return "answer";
  };
  const type = getType(value);

  const select = (t) => {
    if (t === "answer")        onChange(typeof value === "string" ? value : "");
    if (t === "not_available") onChange({ type: "not_available", explanation: (value?.explanation || "") });
    if (t === "does_not_apply") onChange({ type: "does_not_apply" });
  };

  const btnBase = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors";
  const active  = "bg-teal-600 border-teal-600 text-white";
  const idle    = "bg-white border-slate-200 text-slate-600 hover:bg-slate-50";

  return (
    <div>
      <label className="block text-sm font-medium text-slate-800 mb-2" data-testid={`psr-round2-q-${code}`}>
        {questionText}{required && <span className="text-rose-500 ml-1">*</span>}
      </label>
      {/* LARI enrichment badge + historical note for supplemental questions (Phase 2B) */}
      {prefillMeta && (
        <div className="mb-2">
          <LariBadge tier={prefillMeta.source_field_status || "unknown"} code={code} suffix="supp" />
          {prefillMeta.requires_current_year_confirmation && (
            <HistoricalNote value={prefillMeta.historical_value} code={code} suffix="supp" />
          )}
        </div>
      )}
      {/* State selector */}
      <div className="flex flex-wrap gap-2 mb-3" data-testid={`psr-round2-state-${code}`}>
        <button type="button" onClick={() => select("answer")}
          className={`${btnBase} ${type === "answer" ? active : idle}`}
          data-testid={`psr-round2-state-answer-${code}`}>
          <Icons.PenLine className="h-3.5 w-3.5" />Provide Answer
        </button>
        <button type="button" onClick={() => select("not_available")}
          className={`${btnBase} ${type === "not_available" ? active : idle}`}
          data-testid={`psr-round2-state-not-available-${code}`}>
          <Icons.HelpCircle className="h-3.5 w-3.5" />I Don't Have This
        </button>
        <button type="button" onClick={() => select("does_not_apply")}
          className={`${btnBase} ${type === "does_not_apply" ? active : idle}`}
          data-testid={`psr-round2-state-dna-${code}`}>
          <Icons.MinusCircle className="h-3.5 w-3.5" />Does Not Apply
        </button>
      </div>

      {/* No state selected yet */}
      {type === null && (
        <p className="text-xs text-slate-400 italic">Please select one of the options above to respond to this question.</p>
      )}

      {/* Regular text answer */}
      {type === "answer" && (
        <textarea rows={3} value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          placeholder="Type your answer here…"
          data-testid={`psr-round2-field-${code}`} />
      )}

      {/* Not available — optional explanation */}
      {type === "not_available" && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 space-y-2">
          <p className="text-sm text-amber-800 flex items-center gap-1.5">
            <Icons.Info className="h-4 w-4 shrink-0" />
            Noted. If you can explain why this information isn't available, it helps us advise you more accurately.
          </p>
          <textarea rows={2} value={value?.explanation || ""}
            onChange={(e) => onChange({ type: "not_available", explanation: e.target.value })}
            className="w-full border border-amber-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
            placeholder="Optional: explain why this information isn't available…"
            data-testid={`psr-round2-field-${code}`} />
        </div>
      )}

      {/* Does not apply */}
      {type === "does_not_apply" && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-2">
          <Icons.CheckCircle2 className="h-4 w-4 text-teal-600 shrink-0" />
          <p className="text-sm text-slate-600">Marked as not applicable to your situation.</p>
        </div>
      )}
    </div>
  );
}

function Round2View({ data, reload, navigate, lifecycleCard }) {
  const round = data.open_round;
  const [answers, setAnswers] = useState(() => {
    const a = {};
    round.bundles.forEach((b) => b.questions.forEach((q) => { if (q.value != null) a[q.code] = q.value; }));
    return a;
  });
  const [saving, setSaving] = useState("saved");
  const [openR1, setOpenR1] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const timer = useRef(null);
  const pendingRef = useRef({});          // accumulated unsent answer changes
  const fileRefs = useRef({});

  const push = useCallback((next) => {
    // Merge incoming change; later edits to the same key always win.
    pendingRef.current = { ...pendingRef.current, ...next };
    setSaving("saving");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async function flush() {
      timer.current = null;
      // Snapshot and clear before the request so edits arriving during
      // the in-flight window accumulate in pendingRef independently.
      const payload = { ...pendingRef.current };
      pendingRef.current = {};
      try {
        await portalApi.post("/portal/strategic-review/round/answers", { answers: payload });
        setSaving("saved");
        // If edits arrived while the request was in flight, flush them.
        if (Object.keys(pendingRef.current).length > 0 && !timer.current) {
          timer.current = setTimeout(flush, 550);
        }
      } catch {
        // Restore the failed payload; newer in-flight edits for the same key win.
        pendingRef.current = { ...payload, ...pendingRef.current };
        setSaving("saved");
        toast.error("Save failed");
        // Re-schedule so restored unsaved data is not stranded without a timer.
        if (!timer.current && Object.keys(pendingRef.current).length > 0) {
          timer.current = setTimeout(flush, 2000);
        }
      }
    }, 550);
  }, []);
  const setAns = (code, v) => setAnswers((p) => { const n = { ...p, [code]: v }; push({ [code]: v }); return n; });

  const uploadFor = async (rid, file) => {
    const fd = new FormData(); fd.append("file", file); fd.append("request_id", rid);
    try { await portalApi.post("/portal/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Uploaded"); await reload(); }
    catch { toast.error("Upload failed"); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await portalApi.post("/portal/strategic-review/round/submit");
      toast.success("Follow-up submitted — thank you!");
      navigate("/portal");   // completion is now submitted; GET /strategic-review would 404
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
    finally { setSubmitting(false); }
  };

  // BUG-3: derive submit-button readiness from live answers state instead of the
  // static server snapshot (round.required_done), so the button responds immediately
  // when the client completes — or clears — any required supplemental question.
  const isComplete = (v) => {
    if (v == null || v === "") return false;
    if (typeof v === "object") return v.type === "not_available" || v.type === "does_not_apply";
    return true; // non-empty string
  };
  const localRequiredDone = round.bundles
    .flatMap((b) => b.questions)
    .filter((q) => q.required && isComplete(answers[q.code]))
    .length;

  return (
    <div className="max-w-3xl mx-auto" data-testid="psr-round2">
      <div className="bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 mb-5 flex items-start gap-3" data-testid="psr-round2-banner">
        <Icons.AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <h1 className="text-lg font-semibold text-amber-900">Additional information requested</h1>
          <p className="text-amber-700 text-sm">Your questionnaire was submitted successfully. Your tax team needs a few more details to complete your Strategic Review.</p>
        </div>
        <span className="ml-auto text-xs text-slate-500 flex items-center gap-1">{saving === "saving" ? <><Icons.Loader2 className="h-3.5 w-3.5 animate-spin" />Saving…</> : <><Icons.Check className="h-3.5 w-3.5 text-emerald-500" />Saved</>}</span>
      </div>

      <button onClick={() => setOpenR1((o) => !o)} className="w-full flex items-center gap-2 text-sm text-slate-500 bg-white border border-slate-200 rounded-xl px-4 py-3 mb-1.5" data-testid="psr-round1-locked">
        <Icons.Lock className="h-4 w-4" /> Round 1 questionnaire — submitted &amp; locked
        <Icons.ChevronDown className={`h-4 w-4 ml-auto transition-transform ${openR1 ? "rotate-180" : ""}`} />
      </button>

      {/* Round 1 accordion — read-only primary questionnaire with LARI badges (Phase 2B) */}
      {openR1 && (
        <div className="mb-5 bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-4" data-testid="psr-round1-accordion">
          <p className="text-xs font-medium text-slate-500 flex items-center gap-1.5">
            <Icons.Lock className="h-3 w-3" /> Submitted answers — read only
          </p>
          {(data.state?.sections || []).map((sec, si) => (
            <div key={si} data-testid={`psr-r1-section-${si}`}>
              <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">{sec.title}</h3>
              <div className="space-y-2">
                {(sec.questions || []).filter((q) => q.visible && !q.doc_request).map((q) => {
                  const meta = (data.review?.prefill_metadata || {})[q.code];
                  const displayVal = q.value !== undefined && q.value !== null
                    ? (Array.isArray(q.value) ? q.value.join(", ") : String(q.value))
                    : null;
                  return (
                    <div key={q.code} className="bg-white rounded-lg border border-slate-100 px-3 py-2.5"
                      data-testid={`psr-r1-q-${q.code}`}>
                      <p className="text-xs text-slate-500 mb-1">{q.text}</p>
                      {meta && (
                        <>
                          <LariBadge tier={meta.source_field_status || "unknown"} code={q.code} suffix="r1" />
                          {meta.requires_current_year_confirmation && (
                            <HistoricalNote value={meta.historical_value} code={q.code} suffix="r1" />
                          )}
                        </>
                      )}
                      {displayVal
                        ? <p className="text-sm font-medium text-slate-800 mt-1.5">{displayVal}</p>
                        : <p className="text-xs text-slate-400 italic mt-1">Not answered</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lifecycle stepper — same canonical projection used by Dashboard and submitted view */}
      {lifecycleCard && (
        <div className="mb-5" data-testid="psr-round2-lifecycle-banner">
          <StrLifecycleBanner card={lifecycleCard} />
        </div>
      )}

      <div className="space-y-4">
        {round.bundles.map((b, bi) => (
          <div key={bi} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`psr-round2-bundle-${bi}`}>
            <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><Icons.Layers className="h-4 w-4 text-slate-400" />{b.topic}</h2>
            {b.questions.length > 0 && (
              <div className="space-y-5 mb-3">
                {b.questions.map((q) => (
                  <SupplementalQuestion key={q.code} code={q.code} questionText={q.text}
                    value={answers[q.code]} onChange={(v) => setAns(q.code, v)} required={q.required}
                    prefillMeta={q.prefill_meta} />
                ))}
              </div>
            )}
            {b.documents.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-slate-500">Please upload:</p>
                {b.documents.map((d) => {
                  const done = ["uploaded", "awaiting_review", "approved"].includes(d.status);
                  return (
                    <div key={d.id} className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2 border ${done ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid={`psr-round2-doc-${d.id}`}>
                      <span className="text-sm text-slate-700 flex items-center gap-2 min-w-0">{done ? <Icons.CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Icons.Paperclip className="h-4 w-4 text-amber-600" />}<span className="truncate">{d.title}</span></span>
                      {done ? <span className="text-xs text-emerald-700 font-medium">Uploaded ✓</span> : (
                        <>
                          <input ref={(el) => (fileRefs.current[d.id] = el)} type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadFor(d.id, e.target.files[0])} />
                          <button onClick={() => fileRefs.current[d.id]?.click()} className="text-xs bg-white border border-slate-300 rounded-lg px-3 py-1.5 font-medium" data-testid={`psr-round2-upload-${d.id}`}>Upload</button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mt-6">
        <button onClick={() => navigate("/portal")} className="text-slate-500 text-sm">Back to Dashboard</button>
        <button onClick={submit} disabled={submitting || localRequiredDone < round.required_total}
          className="inline-flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 rounded-xl text-sm font-medium disabled:opacity-40" data-testid="psr-round2-submit">
          {submitting ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Send className="h-4 w-4" />}Submit Additional Information
        </button>
      </div>
    </div>
  );
}
